import { useState, useEffect } from '@wordpress/element';
import { renderInputRow } from '../../../utils/renderInputRow';
import apiFetch from '@wordpress/api-fetch';
import { ToastContainer, useToast } from '../ui';

/* ── Reusable Section Card ── */
const SectionCard = ({ icon, title, desc, accent = '#6366f1', children }) => (
    <div className="pqd-card">
        <div className="pqd-card__header" style={{ borderLeftColor: accent }}>
            <div className="pqd-card__header-icon" style={{ background: accent + '18' }}>
                <span style={{ fontSize: 17 }}>{icon}</span>
            </div>
            <div>
                <div className="pqd-card__title">{title}</div>
                {desc && <div className="pqd-card__desc">{desc}</div>}
            </div>
        </div>
        <div className="pqd-card__body">{children}</div>
    </div>
);

/* ── Select2 field row (keeps the jQuery Select2 elements intact) ── */
const Select2Row = ({ label, id }) => (
    <div className="pqd-field-row">
        <div className="pqd-field-label">{label}<span className="pqd-pro-badge"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 7l4 4 5-7 5 7 4-4-2 13H5L3 7z"/></svg>PRO</span>
        </div>
        <div className="pqd-field-control pqd-field-control--select2">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ pointerEvents: 'none', opacity: 0.5, flex: '0 0 420px', maxWidth: '420px' }}>
                <select id={id} className="pqd-select2-target" multiple="multiple" style={{ width: '420px' }} />
            </div>
                <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    This Option Available in{' '}
                    <a
                        href="https://www.plugin999.com/plugin/product-quantity-dropdown-for-woocommerce/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#6366f1', fontWeight: 600, textDecoration: 'underline' }}
                        onClick={e => e.stopPropagation()}
                    >
                        Pro Version
                    </a>
                </span>
            </div>
        </div>
    </div>
);

const AdminTab = () => {
    const [originalSettings, setOriginalSettings] = useState({});
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(false);
    const { toasts, toast, removeToast } = useToast();

    useEffect(() => {
        const global = window.PQDFW_GLOBAL_OPTIONS || {};
        setSettings(global);
        setOriginalSettings(global);
    }, []);

    /* ── Select2 initialisation (identical logic, preserved) ── */
    useEffect(() => {
        if (settings.quantity_product_rule !== 'specific_product') return;

        const configs = [
            { id: '#pqdfw_select_product', action: 'pqdfw_product_ajax', minInput: 3,  saveKey: 'pqdfw_select2'      },
            { id: '#pqdfw_select_cats',    action: 'pqdfw_cats_ajax',    minInput: 3,  saveKey: 'pqdfw_cats_select2'  },
            { id: '#pqdfw_select_tags',    action: 'pqdfw_tags_ajax',    minInput: 0,  saveKey: 'pqdfw_tags_select2'  },
        ];

        const initSelect = (selector, action, minInput, saveKey) => {
            const $el = jQuery(selector);
            $el.select2({
                ajax: {
                    url: ajaxurl,
                    dataType: 'json',
                    delay: 250,
                    data: params => ({ q: params.term, action, nonce: PQDFW_DATA.ajaxnonce }),
                    processResults: data => ({
                        results: (data || []).map(item => ({ id: item[0], text: item[1], price: item[2] || null })),
                    }),
                    cache: true,
                },
                minimumInputLength: minInput,
            });

            $el.on('select2:select select2:unselect', function () {
                const cleaned = $el.select2('data').map(item => ({ id: item.id, text: item.text }));
                updateSetting(saveKey, cleaned);
            });

            const saved = settings[saveKey];
            if (saved && saved.length > 0) {
                $el.empty();
                saved.forEach(item => {
                    const opt = new Option(item.text, item.id, true, true);
                    $el.append(opt);
                });
                $el.trigger('change');
            }
        };

        setTimeout(() => {
            configs.forEach(cfg => initSelect(cfg.id, cfg.action, cfg.minInput, cfg.saveKey));
        }, 50);
    }, [settings.quantity_product_rule]);

    /* ── Save (identical logic, alert → toast) ── */
    const handleSave = () => {
        const normalize = val => {
            if (Array.isArray(val)) {
                return val.map(item =>
                    typeof item === 'object' && item !== null
                        ? { id: String(item.id), text: String(item.text) }
                        : { id: String(item), text: String(item) }
                );
            }
            return val;
        };

        const isEqual = (a, b) => {
            const na = normalize(a);
            const nb = normalize(b);
            if (Array.isArray(na) && Array.isArray(nb)) {
                if (na.length !== nb.length) return false;
                return na.every((v, i) => JSON.stringify(v) === JSON.stringify(nb[i]));
            }
            return na === nb;
        };

        const changedData = Object.fromEntries(
            Object.entries(settings).filter(([key, value]) => !isEqual(originalSettings[key], value))
        );

        if (Object.keys(changedData).length === 0) {
            toast.warning('No changes detected.');
            return;
        }

        setLoading(true);
        apiFetch({ path: '/wp/v2/settings', method: 'POST', data: changedData })
            .then(() => {
                setLoading(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                toast.success('Settings saved successfully!');
                window.PQDFW_GLOBAL_OPTIONS = { ...(window.PQDFW_GLOBAL_OPTIONS || {}), ...settings };
                setOriginalSettings(settings);
            })
            .catch(err => {
                console.error('Error saving settings:', err);
                setLoading(false);
                toast.error('Error saving settings.');
            });
    };

    const updateSetting = (key, value) =>
        setSettings(prev => ({ ...prev, [key]: value }));

    const isSpecific = settings.quantity_product_rule === 'specific_product';

    return (
        <div id="pqd-admin-root">
            <ToastContainer toasts={toasts} onRemove={removeToast} />

            <div className="pqd-page">

                {/* Loader */}
                {loading && (
                    <div className="pqd-overlay-loader">
                        <div className="pqd-spinner-wrap">
                            <div className="pqd-spinner" />
                            <span className="pqd-spinner-label">Saving changes…</span>
                        </div>
                    </div>
                )}

                {/* Page Header */}
                <div className="pqd-page-header">
                    <div className="pqd-page-header__left">
                        <div className="pqd-page-header__icon">
                            <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: '#fff' }}>
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
                            </svg>
                        </div>
                        <div>
                            <h1 className="pqd-page-header__title">Product Quantity Dropdown</h1>
                            <p className="pqd-page-header__sub">Configure dropdown quantity rules for WooCommerce products</p>
                        </div>
                    </div>
                    <div className="pqd-page-header__actions">
                        <a href="https://www.plugin999.com/plugin/product-quantity-dropdown-for-woocommerce/" target="_blank" rel="noreferrer" className="pqd-btn-secondary">
                            &#128172; Pro Version
                        </a>
                        <a href="https://www.plugin999.com/support/" target="_blank" rel="noreferrer" className="pqd-btn-secondary">
                            &#128172; Support
                        </a>
                        <a href="https://wordpress.org/support/plugin/product-quantity-dropdown-for-woocommerce/reviews/" target="_blank" rel="noreferrer" className="pqd-btn">
                            &#11088; Rate Plugin
                        </a>
                    </div>
                </div>

                {/* ── Plugin Enable ── */}
                <SectionCard
                    icon="🔌"
                    title="Plugin Status"
                    desc="Enable or disable the quantity dropdown globally."
                    accent="#6366f1"
                >
                    {renderInputRow('Enable Plugin', 'enable_plugin', 'checkBox', [], 'Enable plugin', settings, updateSetting)}
                </SectionCard>

                {/* ── Product Rule ── */}
                <SectionCard
                    icon="🎯"
                    title="Quantity Rule"
                    desc="Choose whether the dropdown applies to all products or only specific ones."
                    accent="#10b981"
                >
                    {renderInputRow('Apply To', 'quantity_product_rule', 'radio', [
                        { label: 'All Products',      value: 'all_product'      },
                        { label: 'Specific Products', value: 'specific_product' },
                    ], '', settings, updateSetting)}

                    {isSpecific && (
                        <div className="pqd-specific-fields">
                            <div className="pqd-specific-fields__label">
                                <span className="pqd-specific-fields__icon">⚠</span>
                                Search and select the specific products, categories, or tags below.
                            </div>
                            <Select2Row label="Select Products"           id="pqdfw_select_product" />
                            <Select2Row label="Select Product Categories" id="pqdfw_select_cats"    />
                            <Select2Row label="Select Product Tags"       id="pqdfw_select_tags"    />
                        </div>
                    )}
                </SectionCard>

                {/* ── General Settings ── */}
                <SectionCard
                    icon="⚙️"
                    title="General Settings"
                    desc="Set quantity range, step size, and dropdown label."
                    accent="#f59e0b"
                >
                    {renderInputRow('Minimum Quantity', 'pqdfw_min_quantity', 'number', [], '', settings, updateSetting)}
                    {renderInputRow('Maximum Quantity', 'pqdfw_max_quantity', 'number', [], '', settings, updateSetting)}
                    {renderInputRow('Step', 'pqdfw_step_quantity', 'number', [], '', settings,updateSetting)}
                    {renderInputRow('Drop-Down Label', 'pqdfw_dropdown_lable', '', [], '', settings, updateSetting)}
                    {renderInputRow('Disable Dropdown on Shop / Category Page','disable_shop_category_dropdown', 'checkBox',[], '', settings, updateSetting, true)}
                </SectionCard>

                {/* Save Bar */}
                <div className="pqd-save-bar">
                    <p className="pqd-save-bar__hint">
                        <strong>⚠ Unsaved changes</strong> — Click Save to apply your settings.
                    </p>
                    <div className="pqd-save-bar__actions">
                        <button className="pqd-btn" onClick={handleSave}>✓ Save Changes</button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminTab;
